import { Request } from 'components/Request';
import { Dashboard } from 'components/Dashboard';
import store from 'react/redux/store/store';
import { returnToMatchView, rejectedMatch } from 'react/redux/actions/matchmaking';

// Challenge requests shared by the matchmaking search list, the profile card,
// the chat LFG card and the match-found strip.
//
// Requests that take `{ reportError }` show a failure with the general error
// popup unless it is null (the caller shows it inline), and rethrow it either way.

/** The player dismissed the toxic-behaviour warning; nothing was sent. */
export class ChallengeDeclined extends Error {
	constructor() {
		super('Challenge declined');
		this.name = 'ChallengeDeclined';
	}
}

// Resolves when the request may go ahead: at once, or after the player accepts
// the warning shown for someone with reports of toxic behaviour. A dismissed
// warning rejects with ChallengeDeclined.
function confirmPastToxicWarning(behavior) {
	if(behavior && behavior.hasToxicWarning()) {
		return behavior.showToxicWarning().then(() => undefined, () => {
			throw new ChallengeDeclined();
		});
	}
	return Promise.resolve();
}

function post(action, data, reportError) {
	return Request.apiv1(action, data).catch((failure) => {
		if(reportError) {
			reportError(failure);
		}
		throw failure;
	});
}

function returnToMatchViewIfInMatch(failure) {
	if(failure && failure.responseJSON && failure.responseJSON.in_match) {
		store.dispatch(returnToMatchView());
	}
}

/** Resolves when challenging `searchOwner` may go ahead (see confirmPastToxicWarning). */
export function confirmChallenge(searchOwner) {
	if(!searchOwner) {
		return Promise.reject(new Error('Could not retrieve player from this search'));
	}
	return confirmPastToxicWarning(searchOwner.reported_match_behavior);
}

/** Puts you up as a challenger on `searchOwner`'s search `matchId` and applies the reply. */
export function sendChallenge(searchOwner, matchId, { reportError = Request.generalJsonError } = {}) {
	return Request.apiv1('matchmaking/challenge_player', { player_id: searchOwner.id, match_id: matchId })
		.then((response) => {
			Dashboard.performOpenSearchUpdate(response);
			return response;
		})
		.catch((failure) => {
			returnToMatchViewIfInMatch(failure);
			if(reportError) {
				reportError(failure);
			}
			throw failure;
		});
}

export function challengeErrorText(failure) {
	return (failure && failure.responseJSON && failure.responseJSON.error) || 'Challenge failed. Try again.';
}

// Puts you up as a challenger on someone's open search. A dismissed warning
// rejects with the warning's own rejection.
export function challengeSearch(search, options = {}) {
	const player = search.mainPlayer;
	if(!player) {
		return Promise.reject('Could not retrieve player from this search');
	}
	if(player.reported_match_behavior.hasToxicWarning()) {
		return player.reported_match_behavior.showToxicWarning()
			.then(() => sendChallenge(player, search.id, options));
	}
	return sendChallenge(player, search.id, options);
}

// Challenges a player to a format on a ladder, without an open search to reply to.
export function challengePlayer({ player, ladderId, ranked, gameCount }) {
	const send = () => Request.apiv1('matchmaking/challenge_player', {
		player_id: player.id,
		ladder_id: ladderId,
		ranked: ranked,
		match_count: gameCount,
	})
		.then((response) => {
			Dashboard.performOpenSearchUpdate(response);
		})
		.catch((response) => {
			returnToMatchViewIfInMatch(response);
			Request.generalJsonError(response);
			throw response;
		});
	if(player.reported_match_behavior.hasToxicWarning()) {
		return player.reported_match_behavior.showToxicWarning().then(send);
	}
	return send();
}

/**
 * Accepts or declines a challenge someone sent you (or a doubles invite), from
 * `matchList`. A declined singles challenge leaves the list at once. Resolves
 * with the reply.
 */
export function replyToChallenge(match, accept, matchList, { reportError = Request.generalJsonError } = {}) {
	const data = {
		accept: accept,
		match_id: match.id,
		host_code: Dashboard.retrieveHostCode(),
	};
	let action = accept ? 'matchmaking/accept' : 'matchmaking/reject';
	const isDoubles = match.isDoubles();
	if(isDoubles) {
		action = 'matchmaking/challenge_player';
		data.player_id = match.player2.id; // challenge_player answers doubles invitations
	}
	if(accept) {
		addGaEvent('matchmaking', 'accepting');
	}
	else {
		if(!isDoubles) {
			matchList.removeMatch({ match, instantlyRemove: true });
		}
		store.dispatch(rejectedMatch(match.id));
		addGaEvent('matchmaking', 'declining');
	}
	const confirmed = accept && !isDoubles ? confirmPastToxicWarning(match.player2.reported_match_behavior) : Promise.resolve();
	return confirmed
		.then(() => post(action, data, reportError))
		.then((response) => {
			if(response && response.match) {
				const answeredMatch = matchList.getMatchFromMatchData(response.match);
				if(answeredMatch) {
					delete answeredMatch.player1;
					delete answeredMatch.player2;
				}
				matchList.removeMatch({ match: response.match, instantlyRemove: true });
			}
			Dashboard.performOpenSearchUpdate(response);
			return response;
		});
}

/** Accepts an auto-queue matchup, or declines it and keeps queueing. */
export function replyToAutoQueueMatch(match, statusUpdate, accept, { reportError = Request.generalJsonError } = {}) {
	if(!accept) {
		return post('matchmaking/reject_and_continue_auto_queue', { match_id: match.id }, reportError);
	}
	return confirmPastToxicWarning(statusUpdate && statusUpdate.reported_match_behavior)
		.then(() => post('matchmaking/accept_auto_queue', { match_id: match.id }, reportError));
}

// Withdraws the challenge you sent to a player. A failure shows the general
// error popup and resolves, unless reportError is null: then it rejects.
export function cancelChallenge(playerId, matchList, { reportError = Request.generalJsonError } = {}) {
	const request = Request
		.apiv1('matchmaking/end_challenge', { player_id: playerId })
		.then((response) => {
			if(response.challenges_removed) {
				response.challenges_removed.forEach((challengeId) => {
					matchList.removeMatch({ match: challengeId, instantlyRemove: true });
				});
			}
		});
	return reportError ? request.catch(reportError) : request;
}
